import React, {useEffect, useState, useMemo, useRef} from "react";
import { devLog } from "../../utils/devLog";
import styled, { keyframes, css } from "styled-components";
import { useCheckout } from "../../context/CheckoutContext";
import { useTranslation } from "../../hooks/useTranslation";
import { formatCurrency, formatDate, minorToMajor } from "../../i18n/utils/helpers";
import { darkenColor } from "../../utils/commonUtils";
import { updateSession } from "../../utils/paymentSession";
import { useSubscriptionEstimate } from "../../hooks/useSubscriptionEstimate";
import {
  getAddonUnitPriceMinor,
  resolveEstimateLineDisplay,
  resolveCatalogItemDisplayName,
  findCatalogItemPrice,
  resolveCatalogOrHydratedPrice,
  supportsLineItemQuantity,
  indexEstimateLinesByEntityId,
  computeEstimateSummaryTotals,
  computeSelectedItemsBaseline,
  computeCheckoutDiscount,
} from "../../utils/lineItemDisplay";

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  font-weight: 600;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 16px;
`;

const SummaryItemLabel = styled.div`
  color: ${(props) => (props.$bold ? "#000" : "#666")};
  font-weight: ${(props) => (props.$bold ? "600" : "normal")};
`;

const ItemLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const QuantityText = styled.span`
 font-size: 12px;
  color: #777;
  margin-top: 2px;
  margin-left: 14px;
`;

const SummaryItemValue = styled.div`
  font-weight: ${(props) => (props.$bold ? "600" : "normal")};
`;

const Divider = styled.div`
  height: 1px;
  background-color: #ddd;
  margin: 15px 0;
`;

const NextInvoice = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  display: block;
`;

// Slot where CheckoutComApplePayForm mounts the CKO-rendered Apple Pay button.
// When Apple Pay is the selected method, this div replaces the default
// "Pay and Subscribe" button (Stripe-style). The button is rendered by the
// CKO Flow SDK from the top window — the hosted page is not in an iframe,
// so no postMessage bridge is required.
const ApplePaySlot = styled.div`
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  transition: opacity 0.2s ease;

  /* Make the CKO-injected Apple Pay button fill the slot. CKO injects either
     a native <apple-pay-button> custom element or its own button wrapper. */
  & > * {
    width: 100%;
  }
  & apple-pay-button {
    width: 100%;
    --apple-pay-button-width: 100%;
    --apple-pay-button-height: 48px;
    --apple-pay-button-border-radius: 4px;
  }
`;

const SubscribeButton = styled.button`
  background-color: ${(props) => {
    if (props.disabled) {
      return "#cccccc"; // Always grey when disabled, regardless of backend color
    }
    return props.$color || "#4CAF50"; // Use backend color when enabled
  }};
  color: ${(props) => props.disabled ? "#666666" : "white"};
  border: none;
  border-radius: 4px;
  padding: 15px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: 1; // Always full opacity, color handles the disabled state
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${(props) => {
      if (props.disabled) {
        return "#cccccc"; // Stay grey when disabled
      }
      // Use darkened version of the backend color when enabled and hovered
      return darkenColor(props.$color || "#4CAF50", 15);
    }};
  }

  &:active {
    transform: ${(props) => props.disabled ? "none" : "translateY(1px)"};
  }
`;

const TermsText = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 15px;
  line-height: 1.5;
`;

const CouponSection = styled.div`
  margin-bottom: 16px;
`;

const CouponLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const CouponRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CouponInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #c9c7c7;
  border-radius: 4px;
  font-size: 14px;
`;

const CouponButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background-color: ${(props) => props.$color || "#4CAF50"};
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const CouponError = styled.div`
  color: #d14343;
  font-size: 12px;
  margin-top: 8px;
`;

const AppliedCouponsRow = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const CouponChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  border: 1px solid #d6d8db;
  background: #f5f7fb;
  padding: 4px 10px;
  font-size: 12px;
`;

const RemoveCouponButton = styled.button`
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
`;

const ClearCouponsButton = styled.button`
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
`;

/* ─── Shimmer skeleton ───────────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const shimmerStyles = css`
  background: #f0f2f5;
  background-image: linear-gradient(
    90deg,
    #f0f2f5 0px,
    #e8eaed 40px,
    #f0f2f5 80px
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: 4px;
`;

const SkeletonBar = styled.div`
  ${shimmerStyles}
  height: ${(p) => p.$h || "14px"};
  width: ${(p) => p.$w || "100%"};
  border-radius: ${(p) => p.$r || "4px"};
`;

const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => p.$mb || "16px"};
  gap: 12px;
`;

const OrderSummarySkeleton = () => (
  <div aria-label="Loading order summary" aria-busy="true">
    {/* Plan line item */}
    <SkeletonRow $mb="12px">
      <SkeletonBar $h="14px" $w="55%" />
      <SkeletonBar $h="14px" $w="18%" />
    </SkeletonRow>
    <SkeletonRow $mb="20px">
      <SkeletonBar $h="12px" $w="32%" />
      <SkeletonBar $h="12px" $w="0" />
    </SkeletonRow>
    {/* Discount row */}
    <SkeletonRow $mb="16px">
      <SkeletonBar $h="13px" $w="28%" />
      <SkeletonBar $h="13px" $w="16%" />
    </SkeletonRow>
    {/* Divider */}
    <SkeletonBar $h="1px" $w="100%" $r="0" style={{ marginBottom: "16px", opacity: 0.6 }} />
    {/* Total */}
    <SkeletonRow $mb="20px">
      <SkeletonBar $h="15px" $w="22%" />
      <SkeletonBar $h="15px" $w="20%" />
    </SkeletonRow>
    {/* Next invoice */}
    <SkeletonBar $h="12px" $w="58%" style={{ marginBottom: "20px" }} />
  </div>
);

const OrderSummary = ({
                        isFormValid,
                        onSubmit,
                        itemDetails,
                        checkoutPageConfDto,
                        checkoutPageFields,
                        platformItemDetails,
                        formData,
                        billingConnectionId,
                        planId,
                        addons,
                        selectedAddons,
                        disabled = false,
                        sessionId,
                        presetCouponCodes = [],
                        hideSubmitButton = false,
                      }) => {
  const {name, price, period, periodUnit, currencyCode} = itemDetails;
  const { t, language } = useTranslation();

  const [orderDetails, setOrderDetails] = useState({});
  const platformItem = platformItemDetails?.[0];

  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState(() => {
    if (!Array.isArray(presetCouponCodes)) return [];
    return Array.from(
      new Set(
        presetCouponCodes
          .map((code) => code?.trim())
          .filter((code) => !!code)
      )
    );
  });
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const {
    setLogoUrl,
    setBrandColor,
    setMerchantName,
    setEstimatedOrder,
    clientCountry,
    transactionStatus,
  } = useCheckout();
  const [isProcessing, setIsProcessing] = useState(false);
  const isCouponEnabled = checkoutPageFields?.includes("COUPON_CODE");

  const normalizedPresetCoupons = useMemo(() => {
    if (!Array.isArray(presetCouponCodes)) return [];
    return Array.from(
      new Set(
        presetCouponCodes
          .map((code) => code?.trim())
          .filter((code) => !!code)
      )
    );
  }, [presetCouponCodes]);

  // If consumer coupon entry is disabled, keep using preset link coupons.
  const effectiveCouponCodes = useMemo(() => {
    return appliedCoupons.length > 0 ? appliedCoupons : normalizedPresetCoupons;
  }, [appliedCoupons, normalizedPresetCoupons]);

  const { estimatedOrder: localEstimatedOrder, isEstimating } = useSubscriptionEstimate({
    billingConnectionId,
    planId,
    platformItemDetails,
    platformItem,
    selectedAddons,
    formData,
    clientCountry,
    effectiveCouponCodes,
    setEstimatedOrder,
    checkoutCurrency: currencyCode,
    billingPlatformName:
      itemDetails?.billingPlatformName ||
      checkoutPageConfDto?.billingPlatformName ||
      platformItem?.billingPlatformName,
  });

  const calculateNextInvoiceDate = (period, periodUnit) => {
    const now = new Date();
    switch (periodUnit?.toLowerCase()) {
      case "day":
        now.setDate(now.getDate() + parseInt(period));
        break;
      case "week":
        now.setDate(now.getDate() + parseInt(period) * 7);
        break;
      case "month":
        now.setMonth(now.getMonth() + parseInt(period));
        break;
      case "year":
        now.setFullYear(now.getFullYear() + parseInt(period));
        break;
      default:
        return formatDate(now, language);
    }
    return formatDate(now, language);
  };

  const { triggerPaymentEvent, paymentMethodName } = useCheckout();
  const isApplePaySelected = paymentMethodName === "applepay";
  const isProcessingRef = useRef(false);

  const handleClick = async () => {
    if (!isFormValid || disabled) return;

      isProcessingRef.current = true;
      setIsProcessing(true);
    try {
      // Let payment method validate/tokenize first
      await triggerPaymentEvent?.();
      // Then actually submit checkout
      await onSubmit?.();
    } catch (err) {
      console.error("Payment failed:", err);
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;
    }
  };

  // devLog("OrderSummary props:", {
  //   billingConnectionId,
  //   planId,
  //   platformItemDetails,
  // });

  const normalizeCoupon = (value) => value?.trim();

  const isDuplicateCoupon = (code) =>
    appliedCoupons.some((existing) => existing.toLowerCase() === code.toLowerCase());

  const syncCouponState = async (nextCoupons) => {
    setCouponError("");
    setIsApplyingCoupon(true);
    try {
      if (sessionId) {
        await updateSession(sessionId, {
          billingData: {
            couponCodes: nextCoupons,
          },
        });
      }
      setAppliedCoupons(nextCoupons);
    } catch (error) {
      setCouponError(t('checkout.orderSummary.couponApplyFailed'));
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRedeemCoupon = async () => {
    const normalizedCoupon = normalizeCoupon(couponInput);
    if (!normalizedCoupon) {
      setCouponError(t('checkout.orderSummary.couponEmpty'));
      return;
    }
    if (isDuplicateCoupon(normalizedCoupon)) {
      setCouponError(t('checkout.orderSummary.couponDuplicate'));
      return;
    }

    const nextCoupons = [...appliedCoupons, normalizedCoupon];
    await syncCouponState(nextCoupons);
    setCouponInput("");
  };

  const handleRemoveCoupon = async (couponToRemove) => {
    const nextCoupons = appliedCoupons.filter((coupon) => coupon !== couponToRemove);
    await syncCouponState(nextCoupons);
  };

  const handleClearCoupons = async () => {
    if (!appliedCoupons.length) return;
    await syncCouponState([]);
  };

  useEffect(() => {
    if (!normalizedPresetCoupons.length) return;
    const isSame =
      normalizedPresetCoupons.length === appliedCoupons.length &&
      normalizedPresetCoupons.every((code, idx) => code === appliedCoupons[idx]);
    if (!isSame) {
      setAppliedCoupons(normalizedPresetCoupons);
    }
    // We intentionally don't include `appliedCoupons` to avoid resetting
    // user edits while typing/redeeming when URL params are unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedPresetCoupons]);

  useEffect(() => {
    if (transactionStatus === "fail") {
      devLog("Payment Error in OrderSummary:", transactionStatus);
      setIsProcessing(false);
    }
  }, [transactionStatus, setIsProcessing]);

  useEffect(() => {
    const platformItem = platformItemDetails?.[0];

    if (!itemDetails || !platformItem) return;

    const currency = platformItem.currencyCode;
    const amount = minorToMajor(platformItem.price || 0, currency);

    const formattedAmount = formatCurrency(amount, currency, language);

    setOrderDetails({
      planName: platformItem.planName || "Plan",
      planPrice: `${formattedAmount}/${platformItem.periodUnit || ""}`,
      total: formattedAmount,
      nextInvoiceDate: calculateNextInvoiceDate(
        platformItem.period,
        platformItem.periodUnit
      ),
      nextInvoiceAmount: formattedAmount,
    });

  }, [platformItemDetails, itemDetails, language]);
    useEffect(() => {
      setLogoUrl(checkoutPageConfDto?.logoUrl || "");
      setBrandColor(checkoutPageConfDto?.colour || "");
      setMerchantName(
        checkoutPageConfDto?.companyName ||
          checkoutPageConfDto?.brand ||
          ""
      );
  }, [
    checkoutPageConfDto?.logoUrl,
    checkoutPageConfDto?.colour,
    checkoutPageConfDto?.companyName,
    checkoutPageConfDto?.brand,
    setLogoUrl,
    setBrandColor,
    setMerchantName,
  ]);

  const billingPlatformName =
    platformItemDetails?.[0]?.billingPlatformName ||
    itemDetails?.billingPlatformName;

  const itemName = (item, index = 0) =>
    resolveCatalogItemDisplayName(item, {
      billingPlatformName,
      fallbackId: item?.entityId || item?.id || item?.itemPriceId,
      index,
    });

  // Helper function to get frequency display
  const getFrequencyDisplay = () => {
    const periodUnit = platformItemDetails?.periodUnit?.toLowerCase();
    switch (periodUnit) {
      case 'day':
        return '/day';
      case 'week':
        return '/week';
      case 'month':
        return '/month';
      case 'year':
        return '/year';
      default:
        return '';
    }
  };

  // Helper function to render line items or original item
  const renderOrderItems = () => {
    if (localEstimatedOrder?.lineItems?.length > 0) {
      // Show estimated line items with taxes
      const frequency = getFrequencyDisplay();
      
      // check is there is tax_exclusive for the tax
      const isTaxExclusive = localEstimatedOrder.priceType === 'tax_exclusive';
      const priceType = localEstimatedOrder.priceType;
      const chargeableLineItems = localEstimatedOrder.lineItems.filter(
        (item) => Number(item?.totalAmount ?? 0) >= 0
      );

      return (
        <>
          {chargeableLineItems.map((item, index) => {
            const { displayTotal, displayUnit, qty, showQuantityBreakdown } =
              resolveEstimateLineDisplay(item, { priceType });
            return (
            <SummaryItem key={item.id || index}>
              <ItemLabelWrapper>
                <SummaryItemLabel>{itemName(item, index)}</SummaryItemLabel>
                {showQuantityBreakdown && (
                  <QuantityText>
                    ({qty} × {formatCurrency(displayUnit, localEstimatedOrder.currency, language)})
                  </QuantityText>
                )}
              </ItemLabelWrapper>
              <SummaryItemValue>
                {formatCurrency(
                  displayTotal,
                  localEstimatedOrder.currency,
                  language
                )}
                {frequency}
              </SummaryItemValue>
            </SummaryItem>
            );
          })}

          {/* Show tax breakdown if there's tax */}
          {isTaxExclusive && localEstimatedOrder.totalTaxAmount > 0 && (
            <SummaryItem>
              <SummaryItemLabel>{t('checkout.orderSummary.tax')}</SummaryItemLabel>
              <SummaryItemValue>
                {formatCurrency(localEstimatedOrder.totalTaxAmount, localEstimatedOrder.currency, language)}
              </SummaryItemValue>
            </SummaryItem>
          )}
        </>
      );
    } else {
      // Show original item without tax breakdown      
      return (
        <>
          {/* Plan */}
          <SummaryItem>
            <SummaryItemLabel>{orderDetails?.planName}</SummaryItemLabel>
            <SummaryItemValue>{orderDetails?.planPrice}</SummaryItemValue>
          </SummaryItem>

          {/* Add-ons */}
          {selectedAddons?.length > 0 && selectedAddons.map((addon, index) => {
            const itemPriceId = addon.itemPriceId || addon.id;
            const catalog = resolveCatalogOrHydratedPrice(
              platformItemDetails,
              itemPriceId,
              addon
            );
            const unitPrice = minorToMajor(
              catalog?.price ?? getAddonUnitPriceMinor(addon),
              currencyCode
            );
            const addonQty = addon.quantity || 1;
            const addonTotal = addonQty * unitPrice;
            const addonLabel = resolveCatalogItemDisplayName(
              { ...catalog, ...addon },
              { billingPlatformName, fallbackId: itemPriceId, index }
            );
            const showQty =
              supportsLineItemQuantity(billingPlatformName, catalog || addon) &&
              addonQty > 1;

            return (
              <SummaryItem key={itemPriceId || index}>
                <ItemLabelWrapper>
                  <SummaryItemLabel>
                    {addonLabel}
                  </SummaryItemLabel>

                  {showQty && (
                    <QuantityText>
                      ({addonQty} × {formatCurrency(unitPrice, currencyCode, language)})
                    </QuantityText>
                  )}
                </ItemLabelWrapper>

                <SummaryItemValue>
                  {formatCurrency(addonTotal, currencyCode, language)}
                  {getFrequencyDisplay()}
                </SummaryItemValue>
              </SummaryItem>
            );
          })}
        </>
      );
    }
  };

  // Helper function to get the total amount
  const getTotalAmount = () => {
    if (localEstimatedOrder?.futureInvoiceAmountDue) {
      return formatCurrency(localEstimatedOrder.futureInvoiceAmountDue, localEstimatedOrder.currency, language);
    }
    if (selectedAddons?.length > 0) {
      const addonsTotal = selectedAddons.reduce((sum, addon) => {
        const unitPrice = minorToMajor(getAddonUnitPriceMinor(addon), currencyCode);
        return sum + unitPrice * (addon.quantity || 1);
      }, 0);

      const planAmount = minorToMajor(
        platformItemDetails?.[0]?.price || 0,
        currencyCode
      );

      const finalTotal = planAmount + addonsTotal;

      return formatCurrency(finalTotal, currencyCode, language);
    }

    return orderDetails?.total;
  };

  const getDiscountAmount = () => {
    if (!localEstimatedOrder) return 0;

    const platformItem = platformItemDetails?.[0];
    const estimateLinesById = indexEstimateLinesByEntityId(
      localEstimatedOrder.lineItems
    );
    const baselineSubtotal = computeSelectedItemsBaseline({
      planId,
      platformItem,
      selectedAddons,
      estimateLinesById,
      priceType: localEstimatedOrder.priceType,
      currencyCode,
    });
    const summaryTotals = computeEstimateSummaryTotals(localEstimatedOrder);
    const catalogBaseline =
      minorToMajor(platformItem?.price || 0, currencyCode) +
      (selectedAddons || []).reduce((sum, addon) => {
        const unitPrice = minorToMajor(getAddonUnitPriceMinor(addon), currencyCode);
        const qty = Number(addon?.quantity || 1);
        return sum + unitPrice * qty;
      }, 0);

    return computeCheckoutDiscount({
      estimatedOrder: localEstimatedOrder,
      baselineSubtotal,
      summaryTotals,
      couponCodes: effectiveCouponCodes,
      catalogBaseline,
    });
  };

  const discountAmount = getDiscountAmount();

  return (
    <SummaryContainer>
      <SummaryTitle>{t('checkout.orderSummary.title')}</SummaryTitle>

      {isCouponEnabled && (
        <CouponSection>
          <CouponLabel htmlFor="couponCode">{t('checkout.orderSummary.couponLabel')}</CouponLabel>
          <CouponRow>
            <CouponInput
              id="couponCode"
              type="text"
              value={couponInput}
              placeholder={t('checkout.orderSummary.couponPlaceholder')}
              onChange={(e) => setCouponInput(e.target.value)}
              disabled={isApplyingCoupon || disabled}
            />
            <CouponButton
              $color={checkoutPageConfDto?.colour}
              disabled={isApplyingCoupon || disabled}
              onClick={handleRedeemCoupon}
              type="button"
            >
              {isApplyingCoupon
                ? t('checkout.orderSummary.couponApplying')
                : t('checkout.orderSummary.couponRedeem')}
            </CouponButton>
          </CouponRow>
          {couponError && <CouponError>{couponError}</CouponError>}
          {appliedCoupons.length > 0 && (
            <AppliedCouponsRow>
              {appliedCoupons.map((coupon) => (
                <CouponChip key={coupon}>
                  <span>{coupon}</span>
                  <RemoveCouponButton
                    type="button"
                    onClick={() => handleRemoveCoupon(coupon)}
                    disabled={isApplyingCoupon}
                    aria-label={t('checkout.orderSummary.couponRemove')}
                  >
                    x
                  </RemoveCouponButton>
                </CouponChip>
              ))}
              <ClearCouponsButton
                type="button"
                onClick={handleClearCoupons}
                disabled={isApplyingCoupon}
              >
                {t('checkout.orderSummary.couponClearAll')}
              </ClearCouponsButton>
            </AppliedCouponsRow>
          )}
        </CouponSection>
      )}

      {/* Skeleton while first-load or any re-estimation is in flight */}
      {isEstimating && !localEstimatedOrder ? (
        <OrderSummarySkeleton />
      ) : (
        <>
          {/* Render line items */}
          {renderOrderItems()}
          {discountAmount > 0 && (
            <SummaryItem>
              <SummaryItemLabel>{t('checkout.orderSummary.discount')}</SummaryItemLabel>
              <SummaryItemValue>
                -{formatCurrency(discountAmount, localEstimatedOrder?.currency || currencyCode, language)}
              </SummaryItemValue>
            </SummaryItem>
          )}

          <Divider />
          <SummaryItem style={{marginBottom:"0"}}>
            <SummaryItemLabel $bold>{t('checkout.orderSummary.total')}</SummaryItemLabel>
            <SummaryItemValue $bold>{getTotalAmount()}</SummaryItemValue>
          </SummaryItem>
          {localEstimatedOrder?.priceType === 'tax_inclusive' && localEstimatedOrder?.totalTaxAmount > 0 && (
            <div style={{ fontSize: "12px", color: "#666", textAlign: "right"}}>
              {t('checkout.orderSummary.includesTax', {
                variables: {
                  amount: formatCurrency(
                    localEstimatedOrder.totalTaxAmount,
                    localEstimatedOrder.currency,
                    language
                  ),
                },
              })}
            </div>
          )}
          <NextInvoice style={{marginTop:"10px"}}>
            <span>{t('checkout.orderSummary.nextInvoice', { variables: { date: orderDetails?.nextInvoiceDate } })}</span>
          </NextInvoice>
        </>
      )}
      {!hideSubmitButton && (
        isApplePaySelected ? (
          // Apple Pay button is rendered by CheckoutComApplePayForm directly into
          // this slot via CKO Flow — no click handler here.
          <ApplePaySlot
            id="altruon-applepay-slot"
            $disabled={!isFormValid || disabled}
            aria-disabled={!isFormValid || disabled}
          />
        ) : (
          <SubscribeButton
            $color={checkoutPageConfDto?.colour}
            disabled={!isFormValid || disabled}
            onClick={handleClick}
          >
            {t('checkout.orderSummary.payButton')}
          </SubscribeButton>
        )
      )}
      {!hideSubmitButton && (
        <TermsText>
          {t('checkout.orderSummary.termsText', {
            variables: {
              companyName: checkoutPageConfDto?.companyName || 'the merchant'
            }
          })}
        </TermsText>
      )}
    </SummaryContainer>
  );

};

export default OrderSummary;
